<!-- ============================================================
  MenuPermissionManagement.vue — head-only matrix page: which usertype
  ('1' admin / '2' coach / '10' customer) may access which menu.

  Rows come from the backend MENU_REGISTRY via /getMenuPermissionMatrix, so a
  menu added in code appears here automatically (shown "not configured" =
  head-only until the head grants access). The head column is always on and
  locked — head rights live in code, never in the DB (lockout-proof); the
  backend rejects any attempt to save usertype '0'.

  Follows the standard admin patterns: .idt table + <id-modal> confirm.
============================================================ -->
<template>
  <div>
    <div class="pg-head">
      <div class="pg-ico"><span class="mdi mdi-shield-key-outline"></span></div>
      <div>
        <div class="id-h1">{{ $t('menuPerm.title') }}</div>
        <div class="pg-sub">{{ $t('menuPerm.subtitle') }}</div>
      </div>
    </div>

    <div class="scard" style="padding:14px">
      <div style="overflow-x:auto">
        <table class="idt">
          <thead>
            <tr>
              <th style="text-align:left">{{ $t('menuPerm.colMenu') }}</th>
              <th style="text-align:center">{{ $t('role.head') }}</th>
              <th v-for="t in TYPES" :key="t" style="text-align:center">{{ $t(ROLE_LABEL_KEYS[t]) }}</th>
            </tr>
          </thead>
          <tbody v-if="loading" class="id-fade-in" key="sk">
            <tr v-for="i in 6" :key="'sk' + i">
              <td v-for="j in 5" :key="j"><div class="id-skel" style="height:18px"></div></td>
            </tr>
          </tbody>
          <tbody v-else class="id-fade-in" key="data">
            <template v-for="grp in groupedMenus" :key="grp.group">
              <tr class="mp-sec">
                <td colspan="5">{{ $t(groupLabelKey(grp.group)) }}</td>
              </tr>
              <tr v-for="m in grp.menus" :key="m.menukey">
                <td style="text-align:left">
                  <span class="mdi" :class="m.icon" style="margin-right:6px;color:var(--c-primary)"></span>
                  <span>{{ $t(m.labelKey) }}</span>
                  <span v-if="!m.configured && !m.core" class="mp-chip mp-chip-new">{{ $t('menuPerm.notConfigured') }}</span>
                  <span v-if="m.core === 'always'" class="mp-chip">{{ $t('menuPerm.coreAlwaysHint') }}</span>
                  <span v-if="m.core === 'headonly'" class="mp-chip">{{ $t('menuPerm.coreHeadHint') }}</span>
                  <span v-if="m.external" class="mp-chip" v-hint="$t('menuPerm.externalHint')">SSO</span>
                  <span v-if="m.restrictedByCode === 'headadmin'" class="mp-chip mp-chip-warn"
                    v-hint="$t('menuPerm.restrictedHeadAdmin')"><span class="mdi mdi-lock-outline"></span></span>
                  <span v-else-if="m.restrictedByCode === 'partial'" class="mp-chip mp-chip-warn"
                    v-hint="$t('menuPerm.restrictedByCode')"><span class="mdi mdi-code-tags"></span></span>
                </td>
                <!-- head — core menus locked ON (never hideable, incl. this permissions page → no lockout);
                     other menus are togglable so head can hide them from its OWN sidebar. -->
                <td style="text-align:center">
                  <span v-if="m.core" class="id-check on mp-locked" v-hint="$t('menuPerm.headAlways')"><span class="mdi mdi-check"></span></span>
                  <span v-else class="id-check mp-click" :class="{ on: draft[m.menukey] && draft[m.menukey]['0'] }"
                    @click="toggle(m.menukey, '0')" v-hint="$t('menuPerm.headToggleHint')"><span class="mdi mdi-check"></span></span>
                </td>
                <td v-for="t in TYPES" :key="t" style="text-align:center">
                  <span v-if="m.core" class="id-check mp-locked" :class="{ on: draft[m.menukey] && draft[m.menukey][t] }"
                    v-hint="m.core === 'always' ? $t('menuPerm.coreAlwaysHint') : $t('menuPerm.coreHeadHint')">
                    <span class="mdi mdi-check"></span></span>
                  <span v-else-if="isCodeLocked(m, t)" class="id-check mp-locked"
                    v-hint="$t('menuPerm.lockedHeadAdmin')"><span class="mdi mdi-check"></span></span>
                  <span v-else class="id-check mp-click" :class="{ on: draft[m.menukey] && draft[m.menukey][t] }"
                    @click="toggle(m.menukey, t)"><span class="mdi mdi-check"></span></span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div class="mp-foot">
        <span v-if="dirtyCount" class="mp-dirty">{{ $t('menuPerm.changedCount', { n: dirtyCount }) }}</span>
        <button class="id-btn id-btn-primary" :disabled="!dirtyCount" @click="dialogConfirm = true">
          <span class="mdi mdi-content-save"></span> {{ $t('menuPerm.save') }}</button>
      </div>
    </div>

    <!-- per-user grants: เปิดเมนูให้พนักงานบางคนเป็นราย ๆ (ซ้อนบนสิทธิ์ราย role) -->
    <div class="scard" style="padding:14px;margin-top:16px">
      <div class="mp-uhead">
        <div>
          <div class="strong" style="font-size:15px"><span class="mdi mdi-account-key-outline" style="margin-right:6px;color:var(--c-primary)"></span>{{ $t('menuPerm.userTitle') }}</div>
          <div class="pg-sub" style="margin:2px 0 0">{{ $t('menuPerm.userSubtitle') }}</div>
        </div>
        <id-select v-model="selectedUser" searchable :options="userOptions"
          :placeholder="$t('menuPerm.pickUser')" :search-placeholder="$t('gymnasts.typeUsername')"
          style="min-width:260px" @update:model-value="onSelectUser"></id-select>
      </div>

      <div v-if="selectedUser" style="overflow-x:auto;margin-top:12px">
        <table class="idt">
          <thead>
            <tr><th style="text-align:left">{{ $t('menuPerm.colMenu') }}</th><th style="text-align:center">{{ $t('menuPerm.allow') }}</th></tr>
          </thead>
          <tbody class="id-fade-in" :key="selectedUser">
            <template v-for="grp in groupedUserMenus" :key="'u' + grp.group">
              <tr class="mp-sec"><td colspan="2">{{ $t(groupLabelKey(grp.group)) }}</td></tr>
              <tr v-for="m in grp.menus" :key="'u' + m.menukey">
                <td style="text-align:left">
                  <span class="mdi" :class="m.icon" style="margin-right:6px;color:var(--c-primary)"></span>
                  <span>{{ $t(m.labelKey) }}</span>
                  <span v-if="m.restrictedByCode === 'headadmin'" class="mp-chip mp-chip-warn"
                    v-hint="$t('menuPerm.restrictedHeadAdmin')"><span class="mdi mdi-lock-outline"></span></span>
                  <span v-else-if="m.restrictedByCode === 'partial'" class="mp-chip mp-chip-warn"
                    v-hint="$t('menuPerm.restrictedByCode')"><span class="mdi mdi-code-tags"></span></span>
                </td>
                <td style="text-align:center">
                  <span v-if="isUserCodeLocked(m)" class="id-check mp-locked"
                    v-hint="$t('menuPerm.lockedHeadAdmin')"><span class="mdi mdi-check"></span></span>
                  <span v-else class="id-check mp-click" :class="{ on: userDraft[m.menukey] }"
                    @click="toggleUserGrant(m.menukey)"><span class="mdi mdi-check"></span></span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <div class="mp-foot">
          <span v-if="userDirtyCount" class="mp-dirty">{{ $t('menuPerm.changedCount', { n: userDirtyCount }) }}</span>
          <button class="id-btn id-btn-primary" :disabled="!userDirtyCount" @click="saveUserGrants">
            <span class="mdi mdi-content-save"></span> {{ $t('menuPerm.save') }}</button>
        </div>
      </div>
      <div v-else class="pg-sub" style="margin-top:12px">{{ $t('menuPerm.pickUserHint') }}</div>
    </div>

    <!-- save confirm dialog -->
    <id-modal v-model="dialogConfirm" size="md" icon="mdi-shield-key-outline" :title="$t('menuPerm.confirmTitle')" persistent>
      <p style="margin:0 0 8px">{{ $t('menuPerm.confirmBody', { n: dirtyCount }) }}</p>
      <ul style="margin:0;padding-left:20px">
        <li v-for="(c, i) in changedList" :key="i">
          {{ $t(c.labelKey) }} — {{ c.roleLabel }}:
          <strong :style="{ color: c.allowed ? 'var(--c-success)' : 'var(--c-error)' }">
            {{ c.allowed ? $t('menuPerm.allow') : $t('menuPerm.deny') }}</strong>
        </li>
      </ul>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="dialogConfirm = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" @click="save">
          <span class="mdi mdi-content-save"></span> {{ $t('btn.save') }}</button>
      </template>
    </id-modal>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'

// usertypes: head ('0') first (always-on, locked) then the configurable ones
const TYPES = ['1', '2', '10'] // configurable — head ('0') is intentionally absent
const ROLE_LABEL_KEYS = { '0': 'role.head', '1': 'role.admin', '2': 'role.coach', '10': 'role.customer' }
const GROUP_LABEL_KEYS = {
  mine: 'nav.groupMine', overview: 'nav.groupOverview', schedule: 'nav.groupSchedule',
  coach: 'nav.groupCoach', students: 'nav.groupStudents', courses: 'nav.groupCourses',
  finance: 'nav.groupFinance', setup: 'nav.groupSetup', account: 'nav.groupAccount',
}

export default {
  data: () => ({
    TYPES,
    ROLE_LABEL_KEYS,
    loading: false,
    dialogConfirm: false,
    menus: [],
    draft: {},    // menukey -> { '1':bool, '2':bool, '10':bool }
    original: {}, // snapshot for dirty diff
    // per-user grants
    userMenus: [],   // configurable (non-core) menus
    staffUsers: [],  // admin/coach accounts + their per-user grants
    selectedUser: '',
    userDraft: {},    // menukey -> bool (for the selected user)
    userOriginal: {},
  }),

  computed: {
    ...mapGetters({ token: 'getToken' }),
    // group in the order menus arrive from the backend (registry order) — never drops a
    // group the way a hardcoded order array would if a new group is added to the registry
    groupedMenus() {
      const order = []
      const byGroup = {}
      for (const m of this.menus) {
        if (!byGroup[m.group]) { byGroup[m.group] = []; order.push(m.group) }
        byGroup[m.group].push(m)
      }
      return order.map((g) => ({ group: g, menus: byGroup[g] }))
    },
    changedList() {
      const out = []
      const cols = ['0', ...TYPES]   // head ('0') is configurable too (non-core menus only)
      for (const m of this.menus) {
        if (m.core) continue
        for (const t of cols) {
          if (this.draft[m.menukey] && this.original[m.menukey]
            && this.draft[m.menukey][t] !== this.original[m.menukey][t]) {
            out.push({ menukey: m.menukey, labelKey: m.labelKey, usertype: t, roleLabel: this.$t(ROLE_LABEL_KEYS[t]), allowed: this.draft[m.menukey][t] })
          }
        }
      }
      return out
    },
    dirtyCount() { return this.changedList.length },

    // per-user grants
    userOptions() {
      return this.staffUsers.map((u) => ({
        value: u.username,
        label: `${u.username} — ${(`${u.firstname || ''} ${u.lastname || ''}`).trim()} (${this.$t(ROLE_LABEL_KEYS[u.usertype] || 'role.coach')})`,
      }))
    },
    groupedUserMenus() {
      const order = []
      const byGroup = {}
      for (const m of this.userMenus) {
        if (!byGroup[m.group]) { byGroup[m.group] = []; order.push(m.group) }
        byGroup[m.group].push(m)
      }
      return order.map((g) => ({ group: g, menus: byGroup[g] }))
    },
    userDirtyCount() {
      return this.userMenus.filter((m) => !!this.userDraft[m.menukey] !== !!this.userOriginal[m.menukey]).length
    },
  },

  async created() {
    await this.initialize()
    await this.loadUserGrants()
  },

  methods: {
    groupLabelKey(group) { return GROUP_LABEL_KEYS[group] || group },

    async initialize() {
      this.loading = true
      try {
        const res = await axios.post(this.baseURL + '/getMenuPermissionMatrix', {}, {
          headers: { Authorization: `Bearer ${this.token}` },
        })
        if (res.data.success) {
          this.menus = res.data.menus
          const draft = {}
          const original = {}
          for (const m of this.menus) {
            draft[m.menukey] = { 0: !!m.effective['0'], 1: !!m.effective['1'], 2: !!m.effective['2'], 10: !!m.effective['10'] }
            original[m.menukey] = { ...draft[m.menukey] }
          }
          this.draft = draft
          this.original = original
        } else {
          this.$emit('onErrorHandler', res.data.message || this.$t('menuPerm.loadFail'))
        }
      } catch (err) {
        // 401/403 = ไม่ใช่ head หลุดเข้ามา (หรือ session หมดอายุ) — แจ้งแล้วพากลับหน้าที่เข้าได้
        if (err.response && [401, 403].includes(err.response?.status)) {
          this.$emit('onErrorHandler', this.$t('common.noAccess'))
          this.$emit('onClickChangeState', 'dashboard')
        } else {
          this.$emit('onErrorHandler', this.$t('menuPerm.loadFail'))
        }
      }
      this.loading = false
    },

    toggle(menukey, t) {
      if (!this.draft[menukey]) return
      this.draft[menukey][t] = !this.draft[menukey][t]
    },
    // เมนู 'headadmin' (backend ส่ง lockedTypes มา) — ช่องของ role ที่โค้ดบล็อกอยู่แล้วล็อกไว้ กดเปิดไม่ได้
    isCodeLocked(m, t) {
      return Array.isArray(m.lockedTypes) && m.lockedTypes.includes(t)
    },
    // ฝั่งสิทธิ์รายบุคคล: ล็อกถ้าพนักงานที่เลือกเป็น role ที่โค้ดบล็อก (เช่น โค้ช) สำหรับเมนู headadmin
    isUserCodeLocked(m) {
      if (!Array.isArray(m.lockedTypes) || !m.lockedTypes.length) return false
      const u = this.staffUsers.find((x) => x.username === this.selectedUser)
      return !!u && m.lockedTypes.includes(String(u.usertype))
    },

    // ── per-user grants ──────────────────────────────────────
    async loadUserGrants() {
      try {
        const res = await axios.post(this.baseURL + '/getUserMenuGrants', {}, {
          headers: { Authorization: `Bearer ${this.token}` },
        })
        if (res.data.success) {
          this.userMenus = res.data.menus
          this.staffUsers = res.data.users
          // ถ้าเลือก user อยู่แล้ว โหลด grant ใหม่
          if (this.selectedUser) this.onSelectUser(this.selectedUser)
        }
      } catch (err) { /* หน้านี้ head-only อยู่แล้ว — ถ้าโหลดไม่ได้ปล่อยว่าง */ }
    },
    onSelectUser(username) {
      this.selectedUser = username
      const u = this.staffUsers.find((x) => x.username === username)
      const grants = (u && u.grants) || {}
      const draft = {}
      const original = {}
      for (const m of this.userMenus) {
        draft[m.menukey] = !!grants[m.menukey]
        original[m.menukey] = !!grants[m.menukey]
      }
      this.userDraft = draft
      this.userOriginal = original
    },
    toggleUserGrant(menukey) {
      this.userDraft[menukey] = !this.userDraft[menukey]
    },
    async saveUserGrants() {
      const grants = this.userMenus
        .filter((m) => !!this.userDraft[m.menukey] !== !!this.userOriginal[m.menukey])
        .map((m) => ({ username: this.selectedUser, menukey: m.menukey, allowed: this.userDraft[m.menukey] ? 1 : 0 }))
      if (!grants.length) return
      this.$emit('onLoading', true)
      try {
        const res = await axios.post(this.baseURL + '/saveUserMenuGrants', { grants }, {
          headers: { Authorization: `Bearer ${this.token}` },
        })
        if (res.data.success) {
          this.$emit('onInfoHandler', this.$t('menuPerm.userSaved'))
          this.$emit('onMenuPermissionSaved') // App.vue re-fetches (เผื่อ head แก้สิทธิ์ของตัวเอง)
          await this.loadUserGrants()
        } else {
          this.$emit('onErrorHandler', res.data.message || this.$t('msg.updateFail'))
        }
      } catch (err) {
        this.$emit('onErrorHandler', err.message)
      }
      this.$emit('onLoading', false)
    },

    async save() {
      this.dialogConfirm = false
      const permissions = this.changedList.map((c) => ({
        menukey: c.menukey, usertype: c.usertype, allowed: c.allowed ? 1 : 0,
      }))
      if (!permissions.length) return
      this.$emit('onLoading', true)
      try {
        const res = await axios.post(this.baseURL + '/saveMenuPermissionMatrix', { permissions }, {
          headers: { Authorization: `Bearer ${this.token}` },
        })
        if (res.data.success) {
          this.$emit('onInfoHandler', this.$t('menuPerm.saved'))
          this.$emit('onMenuPermissionSaved') // App.vue re-fetches its own menu list
          await this.initialize()
        } else {
          this.$emit('onErrorHandler', res.data.message || this.$t('msg.updateFail'))
        }
      } catch (err) {
        this.$emit('onErrorHandler', err.message)
      }
      this.$emit('onLoading', false)
    },
  },
}
</script>

<style scoped>
/* group section row inside the matrix */
.mp-sec td {
  font-weight: 700;
  color: var(--c-text-heading);
  background: color-mix(in srgb, var(--c-primary) 7%, transparent);
  text-align: left;
}
/* clickable / locked checkbox states */
.mp-click { cursor: pointer; }
.mp-locked { opacity: 0.55; cursor: not-allowed; }
/* row hint chips */
.mp-chip {
  display: inline-block; margin-left: 6px; padding: 1px 7px; border-radius: 999px;
  font-size: 11px; background: color-mix(in srgb, var(--c-text-body) 8%, transparent);
  color: var(--c-text-body); vertical-align: 1px; white-space: nowrap;
}
.mp-chip-new { background: color-mix(in srgb, var(--c-warning) 18%, transparent); color: var(--c-warning); }
.mp-chip-warn { background: color-mix(in srgb, var(--c-error) 12%, transparent); color: var(--c-error); }
/* sticky-ish save footer under the table */
.mp-foot {
  display: flex; align-items: center; justify-content: flex-end; gap: 12px;
  padding-top: 12px;
}
.mp-dirty { font-size: 13px; color: var(--c-text-muted); }
/* per-user grants header (title + staff picker) */
.mp-uhead { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
</style>
